import Cookies from 'js-cookie'
import { convertCartToItems } from '@/util/gtm-helpers'
import { VALID_CURRENCIES } from '~/lib/constants/currencies'
import { getCurrency } from '~/util'
import logger from '../lib/logger'

/**
 * Cart.js separates checkout logic and
 * data from the rest of the application,
 * allowing its lifecycle to be managed
 * independently.
 */

export const state = () => ({
  currency: null,
  id: null,
  items: [],
  total: 0,
  coupon: null,
  order_id: null,
  order: null,
  unsaved_coupon: null,
  deep_linked_package: null,
  // this is available client side only
  client_logos: [],
})

export const getters = {
  isEmpty: state => state.items.length === 0,
}

export const actions = {
  async fetchCart(
    { commit, state, dispatch, rootState },
    isCurrencyUpdated = false
  ) {
    const cartId = Cookies.get('logo_cart_id')
    let currency = getCurrency() || 'usd'

    if (!VALID_CURRENCIES.includes(currency)) {
      currency = 'usd'
    }

    let cart

    // First, we check if there was no cart ID set, we need to make a new one
    if (!cartId) {
      try {
        const { data } = await this.$api.post('/api/v3/carts', {
          eid: rootState.enterprise.id,
          currency,
        })

        cart = data
      } catch (error) {
        this.$toast.error('Sorry, failed to create a cart, please try again.')
        this.$sentry.captureException(error)
      }
    }

    try {
      if (cartId && !isCurrencyUpdated) {
        // then, we attempt to get the cart with the cart id if there is no currency update
        const { data } = await this.$api.get(`/api/v3/carts/${cartId}`)

        cart = data
      } else if (cartId && isCurrencyUpdated) {
        // if there is a currency update, we need to update the cart with the new currency
        const { data } = await this.$api.patch(`/api/v3/carts/${cartId}`, {
          currency,
        })

        cart = data
      }
    } catch (error) {
      // If the cart can not be created or updated, we need to make a new one
      Cookies.remove('logo_cart_id')

      await dispatch('fetchCart')
      cart = state.cart
    }

    if (!cart) return

    Cookies.set('logo_cart_id', cart.id)

    commit('setCart', cart)

    // Compare the cart against the logos the user has client side.
    // If any are missing, we need to pull them and store them
    // If any are outdated, such as the user removed them, we should also remove them here

    // These are the source of truth, which is what's currently in the cart
    const incoming_ids = cart.items.filter(item =>
      item.options.logo?.startsWith('logo_')
    )

    // These are all the client_logos that are currently present
    let existing_ids = state.client_logos.map(logo => logo.id)

    // These are client_logo IDs that do not exist in the cart anymore
    const outdated_ids = existing_ids.filter(id => incoming_ids.indexOf(id) < 0)
    logger.debug(`fetch-cart.js: Outdated IDs not in cart: `, outdated_ids)

    // Remove them from client_logos and from the existing ids array
    outdated_ids.forEach(id => {
      commit('removeClientLogo', id)
      existing_ids = existing_ids.filter(existing_id => existing_id !== id)
    })

    // These are the IDs of logos we need to fetch and add to the cart.cient_logos
    const missing_ids = incoming_ids.filter(id => existing_ids.indexOf(id) < 0)
    if (missing_ids.length === 0) {
      logger.debug('fetch-cart.js: No new logos to fetch, returning.')
      return
    }
    logger.debug(`fetch-cart.js: Fetching missing IDs: `, missing_ids)

    // Download the logos.  If the logo cannot be found, dispatch a removal from the cart.
    const logos = await Promise.all(
      missing_ids.map(item =>
        this.$api
          .get(`/api/v2/logos/${item.options.logo}`)
          .then(({ data }) => data)
          .catch(() =>
            cart.items
              .filter(item => item.id === item.options.logo)
              .map(item => dispatch('removeItem', item))
          )
      )
    )

    logos.forEach(logo => !!logo && commit('addClientLogo', logo))

    return cart
  },

  async addItem(
    { commit, state, dispatch },
    { item, package: pkg, price, credit, options }
  ) {
    if (!state.id) {
      await dispatch('fetchCart')
    }

    const itemParams = {
      quantity: 1,
      package: pkg.code,
      price_id: price?.id,
      credit,
      options,
    }

    if (pkg.type === 'kit' || pkg.type === 'plan') {
      await Promise.all(
        state.items
          // Remove anything from the cart that isn't the account sub
          .filter(itm => itm.package.type === 'kit')
          .map(item => dispatch('removeItem', item))
      )
    }

    const { data: cart } = await this.$api.post(
      `/api/v3/carts/${state.id}/items`,
      itemParams
    )

    commit('addClientLogo', item)
    commit('setCart', cart)

    this.$bus.$emit(
      this.$events.EcommerceAddToCart,
      convertCartToItems({
        items: [cart.items[cart.items.length - 1]],
      })
    )
  },

  async updateItem(
    { commit, state, dispatch },
    { item, package: pkg, credit = null, options }
  ) {
    if (!state.id) {
      await dispatch('fetchCart')
    }

    // TODO: I'm trying to match up the item, copy it, and send the whole thing up and to analytics.
    // The should be cleaned up to something a lot more concise, which I dont have the time for atm
    const to_update = {
      ...(state.items.find(i => i.id === item.id) || {}),
      id: item.id,
      quantity: 1,
      package: pkg || item.package,
      credit,
      options,
    }

    const { data: cart } = await this.$api.patch(
      `/api/v3/carts/${state.id}/items/${item.id}`,
      to_update
    )

    commit('addClientLogo', item)
    commit('setCart', cart)
    // The checkout session has been invalidated
    commit('setCheckoutSessionId', null)

    this.$bus.$emit(
      this.$events.EcommerceAddToCart,
      convertCartToItems({
        items: [cart.items.find(({ id }) => id === item.id)],
      })
    )
  },

  async removeItem({ commit, state, dispatch }, item) {
    if (!state.id) {
      await dispatch('fetchCart')
    }

    const { data: cart } = await this.$api.delete(
      `/api/v3/carts/${state.id}/items/${item.id}`
    )

    const ecommerceItems = convertCartToItems({ items: [item] })

    this.$bus.$emit(this.$events.EcommerceRemoveFromCart, ecommerceItems)

    commit('removeClientLogo', item.id)
    commit('setCart', cart)
  },

  async applyCoupon({ commit, state }, code) {
    const { data: cart } = await this.$api.post(
      `/api/v3/carts/${state.id}/coupons/${code}`
    )

    commit('setCart', cart)
  },

  async removeCoupon({ commit, state }) {
    const { data: cart } = await this.$api.delete(
      `/api/v3/carts/${state.id}/coupons`
    )

    commit('setCart', cart)
  },

  async clear({ state, dispatch }) {
    return Promise.all(state.items.map(item => dispatch('removeItem', item)))
  },
}

export const mutations = {
  setOrder(state, order) {
    state.order = order
  },

  setCheckoutSessionId(state, id) {
    state.checkout_session_id = id
  },
  setDeepLinkedPackage(state, pkg) {
    state.deep_linked_package = pkg
  },

  setCoupon(state, coupon) {
    state.coupon = coupon
  },

  setCart(state, cart) {
    state.currency = cart.currency || 'usd'
    state.total = cart.total
    state.items = cart.items
    state.coupon = cart.coupon
    state.id = cart.id
  },

  updateTotal(state, total) {
    state.total = total
  },

  addClientLogo(state, logo) {
    state.client_logos.push(logo)
  },
  removeClientLogo(state, id) {
    state.client_logos = state.client_logos.filter(logo => logo.id !== id)
  },

  reset(state) {
    state.id = null
    state.items = []
    state.total = 0
    state.coupon = null
    state.order_id = null
    state.order = null
    state.client_logos = []
    state.deep_linked_package = null
    state.checkout_session_id = null
  },
}
